import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatMoney, formatDate, resolveArtworkUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    ArrowLeft, Printer, RefreshCw, Loader2, Disc3, ExternalLink, Check,
} from "lucide-react";

const HEADER_TITLE = "Prepared for Insurance: Butterworth 115 Grange Road — Vinyl collection";
const LOCATION_ORDER = ["Ruth", "Andy", "Archive", "Lagom"];
const CONDITION_ABBR = {
    "Mint": "MT",
    "Near Mint": "NM",
    "Excellent": "EX",
    "Good": "GD",
    "Poor": "PR",
};

function groupRowsByLocation(rows) {
    const groups = new Map();
    for (const r of rows) {
        const loc = r.location || "Unassigned";
        if (!groups.has(loc)) groups.set(loc, []);
        groups.get(loc).push(r);
    }
    // Sort with canonical order first, then any extras alphabetically
    const known = LOCATION_ORDER.filter((l) => groups.has(l));
    const extras = [...groups.keys()].filter((l) => !LOCATION_ORDER.includes(l)).sort();
    return [...known, ...extras].map((loc) => {
        const items = groups.get(loc);
        const subtotal = items.reduce(
            (acc, r) => ({
                min: acc.min + (r.min || 0),
                median: acc.median + (r.median || 0),
                max: acc.max + (r.max || 0),
                paid: acc.paid + (r.purchase_price || 0),
            }),
            { min: 0, median: 0, max: 0, paid: 0 },
        );
        return { location: loc, items, subtotal, count: items.length };
    });
}

export default function InsuranceReportPage() {
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = async (refresh = false) => {
        if (refresh) setRefreshing(true); else setLoading(true);
        try {
            const url = refresh ? "/reports/insurance/refresh" : "/reports/insurance";
            const call = refresh ? api.post(url) : api.get(url);
            const { data } = await call;
            setReport(data);
            if (refresh) toast.success("Prices refreshed from Discogs");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to build report");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { load(false); }, []);

    const groups = useMemo(
        () => (report ? groupRowsByLocation(report.rows) : []),
        [report],
    );

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="insurance-loading">
                <Disc3 className="w-10 h-10 text-primary vinyl-spin" strokeWidth={1.5} />
            </div>
        );
    }
    if (!report) return null;

    const generatedDate = formatDate(report.generated_at);
    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="insurance-report">
            {/* Screen-only toolbar */}
            <div className="no-print sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
                <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
                    <Button
                        variant="ghost"
                        onClick={() => navigate("/")}
                        className="gap-2"
                        data-testid="report-back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to collection
                    </Button>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            onClick={() => load(true)}
                            disabled={refreshing}
                            className="gap-2 rounded-full"
                            data-testid="report-refresh-btn"
                        >
                            {refreshing
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <RefreshCw className="w-4 h-4" />}
                            Refresh prices
                        </Button>
                        <Button
                            onClick={() => window.print()}
                            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                            data-testid="report-print-btn"
                        >
                            <Printer className="w-4 h-4" />
                            Print / Save as PDF
                        </Button>
                    </div>
                </div>
            </div>

            <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10 print:py-4 print:px-0 print:max-w-none">
                {/* Report header */}
                <header className="border-b border-border pb-6 mb-6">
                    <p className="overline text-muted-foreground mb-2" data-testid="report-cover-note">
                        {HEADER_TITLE}
                    </p>
                    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-none">
                        Wax &amp; Sleeves
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        Generated {generatedDate} · {report.record_count} records · Currency {report.currency}
                    </p>
                </header>

                {/* Totals summary */}
                <section
                    className="grid grid-cols-3 gap-4 md:gap-6 mb-8 print:mb-6"
                    data-testid="report-totals"
                >
                    <TotalCard label="Minimum" value={report.totals.min} testId="report-total-min" />
                    <TotalCard label="Median" value={report.totals.median} accent testId="report-total-median" />
                    <TotalCard label="Maximum" value={report.totals.max} testId="report-total-max" />
                </section>

                <p className="text-xs text-muted-foreground mb-6 print:mb-4">
                    <span className="font-semibold">{report.priced_records}</span> records priced via Discogs marketplace,
                    <span className="font-semibold"> {report.fallback_records}</span> using purchase price as fallback
                    (no Discogs link or marketplace data). Refresh cache lasts 7 days.
                </p>

                {report.notes && report.notes.length > 0 && (
                    <div
                        className="no-print rounded-md border border-primary/30 bg-primary/5 p-3 text-xs text-foreground/80 mb-6"
                        data-testid="report-notes"
                    >
                        {report.notes.map((n, i) => (
                            <p key={i} className="flex items-start gap-2">
                                <span className="text-primary">•</span> {n}
                                {n.toLowerCase().includes("seller settings") && (
                                    <a
                                        href="https://www.discogs.com/settings/seller"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary underline hover:no-underline ml-1"
                                    >
                                        Open Discogs seller settings ↗
                                    </a>
                                )}
                            </p>
                        ))}
                    </div>
                )}

                {/* Table */}
                <div className="overflow-x-auto rounded-md border border-border insurance-print-wrap print:border-0">
                    <table className="w-full text-sm insurance-print-table" data-testid="report-table">
                        <colgroup>
                            <col className="col-record" />
                            <col className="col-idp" />
                            <col className="col-media" />
                            <col className="col-paid" />
                            <col className="col-min" />
                            <col className="col-median" />
                            <col className="col-max" />
                            <col className="col-graded" />
                        </colgroup>
                        <thead className="bg-secondary/60 text-left overline text-muted-foreground">
                            <tr>
                                <th className="p-3 font-normal">Record</th>
                                <th className="p-3 font-normal text-center">IDP</th>
                                <th className="p-3 font-normal">Media</th>
                                <th className="p-3 font-normal text-right">Paid</th>
                                <th className="p-3 font-normal text-right">Min</th>
                                <th className="p-3 font-normal text-right">Median</th>
                                <th className="p-3 font-normal text-right">Max</th>
                                <th className="p-3 font-normal text-right">Graded</th>
                            </tr>
                        </thead>
                        {groups.map(({ location, items, subtotal, count }) => (
                            <tbody
                                key={location}
                                className="border-t border-border"
                                data-testid={`report-group-${location}`}
                            >
                                <tr className="bg-secondary/40">
                                    <td
                                        className="p-3 font-display font-semibold uppercase tracking-wider text-[0.7rem] text-foreground"
                                        colSpan={8}
                                    >
                                        <span className="inline-flex items-baseline gap-2">
                                            <span>{location}</span>
                                            <span className="text-muted-foreground font-normal normal-case tracking-normal text-xs">
                                                · {count} {count === 1 ? "record" : "records"}
                                            </span>
                                        </span>
                                    </td>
                                </tr>
                                {items.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="border-t border-border align-middle whitespace-nowrap"
                                        data-testid={`report-row-${row.id}`}
                                    >
                                        <td className="p-3 print-record-cell whitespace-nowrap overflow-hidden">
                                            <div className="flex gap-3 items-center min-w-0">
                                                <img
                                                    src={resolveArtworkUrl(row)}
                                                    alt=""
                                                    className="w-8 h-8 rounded-sm object-cover shrink-0 border border-border print-hide-thumb"
                                                    loading="lazy"
                                                />
                                                <span className="min-w-0 flex-1 truncate">
                                                    <span className="font-medium">{row.album_title}</span>
                                                    <span className="text-muted-foreground"> · {row.artist}</span>
                                                    {row.discogs_id && (
                                                        <a
                                                            href={row.discogs_url || `https://www.discogs.com/release/${row.discogs_id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="no-print inline-flex items-center gap-0.5 text-[10px] font-mono text-primary ml-2 hover:underline align-middle"
                                                        >
                                                            #{row.discogs_id}
                                                            <ExternalLink className="w-2.5 h-2.5" />
                                                        </a>
                                                    )}
                                                </span>
                                            </div>
                                        </td>
                                        <td
                                            className="p-3 text-center whitespace-nowrap"
                                            data-testid={`report-idp-${row.id}`}
                                        >
                                            {row.idp ? (
                                                <Check
                                                    className="w-4 h-4 inline text-primary"
                                                    strokeWidth={2.5}
                                                    aria-label="IDP on"
                                                />
                                            ) : (
                                                <span className="text-muted-foreground/30">—</span>
                                            )}
                                        </td>
                                        <td className="p-3 text-muted-foreground whitespace-nowrap">
                                            {CONDITION_ABBR[row.media_condition] || row.media_condition}
                                            {row.limited && <span className="ml-1 text-[10px] font-semibold text-[#C0392B]">·LTD</span>}
                                        </td>
                                        <td className="p-3 text-right font-mono whitespace-nowrap">{formatMoney(row.purchase_price)}</td>
                                        <td className="p-3 text-right font-mono whitespace-nowrap">{formatMoney(row.min)}</td>
                                        <td className="p-3 text-right font-mono font-semibold whitespace-nowrap">{formatMoney(row.median)}</td>
                                        <td className="p-3 text-right font-mono whitespace-nowrap">{formatMoney(row.max)}</td>
                                        <td className="p-3 text-right font-mono text-primary whitespace-nowrap">
                                            {row.graded_price != null ? formatMoney(row.graded_price) : "—"}
                                        </td>
                                    </tr>
                                ))}
                                <tr
                                    className="border-t border-border bg-secondary/25 font-semibold"
                                    data-testid={`report-subtotal-${location}`}
                                >
                                    <td className="p-3 text-muted-foreground" colSpan={3}>
                                        Subtotal · {location}
                                    </td>
                                    <td className="p-3 text-right font-mono">{formatMoney(subtotal.paid)}</td>
                                    <td className="p-3 text-right font-mono">{formatMoney(subtotal.min)}</td>
                                    <td className="p-3 text-right font-mono">{formatMoney(subtotal.median)}</td>
                                    <td className="p-3 text-right font-mono">{formatMoney(subtotal.max)}</td>
                                    <td className="p-3 text-right font-mono">—</td>
                                </tr>
                            </tbody>
                        ))}
                        <tfoot>
                            <tr className="border-t-2 border-foreground/40 bg-secondary/40 font-semibold">
                                <td className="p-3" colSpan={3}>Grand total</td>
                                <td className="p-3 text-right font-mono">—</td>
                                <td className="p-3 text-right font-mono">{formatMoney(report.totals.min)}</td>
                                <td className="p-3 text-right font-mono">{formatMoney(report.totals.median)}</td>
                                <td className="p-3 text-right font-mono">{formatMoney(report.totals.max)}</td>
                                <td className="p-3 text-right font-mono">—</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <footer className="mt-10 pt-6 border-t border-border text-xs text-muted-foreground">
                    <p>
                        Prices sourced from the Discogs marketplace price suggestions API. Values are indicative and reflect
                        market activity at the time of the last refresh; they are not a formal valuation. Records without
                        Discogs data are valued at their recorded purchase price.
                    </p>
                    <p className="mt-2">Report generated by Wax &amp; Sleeves · {generatedDate}</p>
                </footer>
            </main>
        </div>
    );
}

function TotalCard({ label, value, accent, testId }) {
    return (
        <div
            className={`tactile-card rounded-md p-5 md:p-6 print:border print:border-border print:shadow-none ${accent ? "bg-primary/5" : ""}`}
            data-testid={testId}
        >
            <p className="overline text-muted-foreground mb-1">{label}</p>
            <p className="font-display text-3xl md:text-4xl font-bold tracking-tight">{formatMoney(value || 0)}</p>
        </div>
    );
}
